import serviceInterceptor from '../../services/ServiceInterceptor';
import { OTHER_VENDORS_API } from '../../constants';
import { 
  GET_OTHER_VENDORS_ERROR,
  GET_OTHER_VENDOR_BY_ID_ERROR,
  CREATE_OTHER_VENDOR_ERROR,
  UPDATE_OTHER_VENDOR_ERROR,
  DELETE_OTHER_VENDOR_ERROR,
} from '../../constants/error';
import { 
  OtherVendor, 
  CreateOtherVendorRequest, 
  UpdateOtherVendorRequest, 
  OtherVendorResponse,
  ApiError 
} from '../../types/otherVendors';

/**
 * Get all other vendors
 */
export const getAllOtherVendors = async (searchString?: string): Promise<OtherVendor[]> => {
  try {
    const params: any = {};
    if (searchString) {
      params.searchString = searchString;
    }

    const response = await serviceInterceptor.get(OTHER_VENDORS_API, { params });

    if (!response?.data) {
      throw new Error(GET_OTHER_VENDORS_ERROR);
    }

    const result: OtherVendorResponse = response.data;
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch other vendors');
    }

    return Array.isArray(result.data) ? result.data : [];
  } catch (error) {
    console.error('Error fetching other vendors:', error);
    throw new Error(GET_OTHER_VENDORS_ERROR);
  }
};

/**
 * Get other vendor by ID
 */
export const getOtherVendorById = async (id: number): Promise<OtherVendor> => {
  try {
    const response = await serviceInterceptor.get(`${OTHER_VENDORS_API}/${id}`);

    if (!response?.data) {
      throw new Error(GET_OTHER_VENDOR_BY_ID_ERROR);
    }

    const result: OtherVendorResponse = response.data;
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch other vendor');
    }

    if (!result.data || Array.isArray(result.data)) {
      throw new Error('Invalid vendor data received');
    }

    return result.data;
  } catch (error) {
    console.error('Error fetching other vendor:', error);
    throw new Error(GET_OTHER_VENDOR_BY_ID_ERROR);
  }
};

/**
 * Create new other vendor
 */
export const createOtherVendor = async (vendorData: CreateOtherVendorRequest): Promise<OtherVendor> => {
  try {
    const formData = new FormData();
    formData.append('name', vendorData.name);
    formData.append('description', vendorData.description);
    
    if (vendorData.vendor_url) {
      formData.append('vendor_url', vendorData.vendor_url);
    }
    
    if (vendorData.vendor_logo) {
      formData.append('vendor_logo', vendorData.vendor_logo);
    }

    const response = await serviceInterceptor.post(OTHER_VENDORS_API, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response?.data) {
      throw new Error(CREATE_OTHER_VENDOR_ERROR);
    }

    const result: OtherVendorResponse = response.data;
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to create other vendor');
    }

    if (!result.data || Array.isArray(result.data)) {
      throw new Error('Invalid vendor data received');
    }

    return result.data;
  } catch (error) {
    console.error('Error creating other vendor:', error);
    throw new Error(CREATE_OTHER_VENDOR_ERROR);
  }
};

/**
 * Update other vendor
 */
export const updateOtherVendor = async (
  id: number, 
  vendorData: UpdateOtherVendorRequest
): Promise<OtherVendor> => {
  try {
    const formData = new FormData();
    
    if (vendorData.name) {
      formData.append('name', vendorData.name);
    }
    
    if (vendorData.description) {
      formData.append('description', vendorData.description);
    }
    
    if (vendorData.vendor_url) {
      formData.append('vendor_url', vendorData.vendor_url);
    }
    
    if (vendorData.vendor_logo) {
      formData.append('vendor_logo', vendorData.vendor_logo);
    }

    const response = await serviceInterceptor.put(`${OTHER_VENDORS_API}/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response?.data) {
      throw new Error(UPDATE_OTHER_VENDOR_ERROR);
    }

    const result: OtherVendorResponse = response.data;
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to update other vendor');
    }

    if (!result.data || Array.isArray(result.data)) {
      throw new Error('Invalid vendor data received');
    }

    return result.data;
  } catch (error) {
    console.error('Error updating other vendor:', error);
    throw new Error(UPDATE_OTHER_VENDOR_ERROR);
  }
};

/**
 * Delete other vendor
 */
export const deleteOtherVendor = async (id: number): Promise<void> => {
  try {
    const response = await serviceInterceptor.delete(`${OTHER_VENDORS_API}/${id}`);

    if (!response?.data) {
      throw new Error(DELETE_OTHER_VENDOR_ERROR);
    }

    const result: OtherVendorResponse = response.data;
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to delete other vendor');
    }
  } catch (error) {
    console.error('Error deleting other vendor:', error);
    throw new Error(DELETE_OTHER_VENDOR_ERROR);
  }
};
